export function BylawFramework() {
  const frameworkAreas = [
    {
      icon: 'ri-home-4-line',
      title: 'Flexible Zoning Approach',
      description: 'Langley offers more permissive zoning compared to Vancouver, with opportunities in both residential and rural zones depending on jurisdiction and property type.',
      details: [
        'Single-family homes generally permitted',
        'Secondary suites may qualify',
        'Rural properties with farm-stay potential',
        'Check specific zone designation',
        'Strata bylaws still apply'
      ]
    },
    {
      icon: 'ri-file-text-line',
      title: 'Business License Requirements',
      description: 'Both Township and City require business licenses for short-term rental operations, with straightforward application processes and reasonable fees.',
      details: [
        'Annual business license mandatory',
        'Property-specific registration',
        'Owner information disclosure',
        'Insurance verification required',
        'Renewal process simplified'
      ]
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety & Property Standards',
      description: 'Compliance with BC Building Code and fire safety regulations is required, with inspections possible for verification of standards.',
      details: [
        'Smoke and CO detectors mandatory',
        'Fire extinguisher requirements',
        'Emergency exit compliance',
        'Building code adherence',
        'Regular maintenance standards'
      ]
    },
    {
      icon: 'ri-user-line',
      title: 'Owner Occupancy Rules',
      description: 'Unlike Vancouver, Langley does not strictly require owner-occupancy for all short-term rentals, offering more flexibility for investment properties.',
      details: [
        'No blanket owner-occupancy requirement',
        'Investment properties may qualify',
        'Principal residence not always required',
        'Varies by zone and property type',
        'Verify specific property eligibility'
      ]
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Rental Duration Limits',
      description: 'Short-term rentals are typically defined as stays under 30 consecutive days, with no strict annual day limits like some municipalities.',
      details: [
        'Under 30 days = short-term rental',
        'No 90-day annual cap',
        'Year-round operation possible',
        'Seasonal flexibility available',
        'Long-term rental option remains'
      ]
    },
    {
      icon: 'ri-team-line',
      title: 'Good Neighbor Policies',
      description: 'Operators must maintain peaceful enjoyment for neighbors, with complaint mechanisms and enforcement for nuisance issues.',
      details: [
        'Noise bylaw compliance required',
        'Parking regulations must be met',
        'Guest behavior responsibility',
        'Complaint response protocols',
        'Community respect standards'
      ]
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Flexible Bylaw Framework
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Langley's regulatory approach balances property rights with community standards, offering more flexibility than stricter municipalities while maintaining quality standards.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {frameworkAreas.map((area, index) => (
            <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8 hover:shadow-xl transition-all">
              <div className="w-16 h-16 bg-[#882629] rounded-2xl flex items-center justify-center mb-6">
                <i className={`${area.icon} text-white text-3xl`}></i>
              </div>
              
              <h3 className="font-['Fraunces'] text-xl font-bold text-[#505A5A] mb-4">
                {area.title}
              </h3>
              
              <p className="text-[#505A5A]/70 mb-6 leading-relaxed">
                {area.description}
              </p>

              <ul className="space-y-2">
                {area.details.map((detail, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-[#882629] text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center mt-0.5"></i>
                    <span className="text-sm text-[#505A5A]/80">{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-[#F0F0F0] rounded-2xl p-8 lg:p-12">
          <div className="flex flex-col lg:flex-row items-start gap-6">
            <div className="w-16 h-16 bg-[#882629] rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-line text-white text-3xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A] mb-4">
                Why Langley's Framework Works for Investors
              </h3>
              <p className="text-[#505A5A]/70 leading-relaxed mb-4">
                Langley's approach recognizes the economic benefits of short-term rentals while maintaining community standards. The flexible framework allows property owners to maximize their investment potential without the restrictive owner-occupancy requirements found in Vancouver.
              </p>
              <p className="text-[#505A5A]/70 leading-relaxed">
                <strong>Key advantage:</strong> Investment properties can operate as short-term rentals in many zones, making Langley an attractive market for both local and out-of-province investors seeking rental income opportunities in the Lower Mainland.
              </p>
            </div>
          </div>
        </div>

        {/* Enforcement Style */}
        <div className="mt-12 bg-gradient-to-r from-[#882629] to-[#6E1F22] rounded-2xl p-8 lg:p-10 text-white shadow-xl">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <i className="ri-shield-check-line text-6xl"></i>
            </div>
            <div className="flex-grow text-center lg:text-left">
              <h4 className="text-2xl font-bold mb-3">
                Enforcement Style: Provincial Registry + Moderate Oversight
              </h4>
              <p className="text-lg text-white/90 leading-relaxed">
                Langley follows BC's Provincial Registry requirements with automatic platform de-listing for non-compliant properties. The city takes a balanced approach with complaint-based investigations and periodic compliance checks, making it more operator-friendly than Vancouver while maintaining community standards.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
